import { useEffect, useState } from 'react'
import BordFilterCard from './CommonComponent/BordFilterCard'
import BordMegementHeader from './CommonComponent/BordMegementHeader'
import BoardManagmentTable from './CommonComponent/BoardManagmentTable'
import api from '@/services/api'
import { useDebounce } from '@/hooks/useDebounce'
import { toast } from 'react-toastify'
import BordAddandEditDialogComponent from './CommonComponent/BordAddandEditDialogComponent'
import BordDeleteDialogComponent from './CommonComponent/BoradDeleteDialogComponent'
import ActiveInactiveComponent from './CommonComponent/BoradActiveInactiveComponent'
import { IconButton, Pagination, Tooltip } from '@mui/material'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
import UploadCsv from './CommonComponent/UploadCsv'
import ExportCsv from './CommonComponent/ExportCsv'
import { EditIcon } from 'lucide-react'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import ReusableTable from '@/components/ReusableTable/ReusableTable'

const LicenseType = () => {
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [boardData, setBoardData] = useState<any[]>([])
  const [loadingForShimmer, setLoadingForShimmer] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const debouncedSearch = useDebounce(searchKeyword, 500)
  const [openDialog, setOpenDialog] = useState(false)
  const [addDialogLoading, setAddDialogLoading] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [selectedBoardForDelete, setSelectedBoardForDelete] =
    useState<any>(null)
  const [openToggle, setOpenToggle] = useState(false)
  const [toggleData, setToggleData] = useState<any>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })
  const [editData, setEditData] = useState<any>(null)
  const [sortField, setSortField] = useState<any>("")
  const [sortOrder, setSortOrder] = useState("asc")

  useEffect(() => {
    fetchBoardName(currentPage)
  }, [debouncedSearch, pageSize, currentPage,sortField,sortOrder])

  const fetchBoardName = async (page?: number) => {
    setLoading(true)
    setError('')
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        page: page || currentPage,
        limit: pageSize,
      }

      if (searchKeyword?.trim()) {
        params.search = searchKeyword
      }
      if(sortField){
        params.sortField = sortField
      }
      if(sortOrder){
        params.sortField = sortOrder
      }

      const response = await api.get(`boards/licensetype/list`, {
        params,
      })

      if (response?.data?.data) {
        setBoardData(response?.data?.data)
      } else {
        setError('No patients data found')
      }

      const pagination = response?.data?.pagination
      if (pagination) {
        setTotalPages(Number(pagination.totalPages) || 1)
        setTotalRecords(Number(pagination.total) || 0)
      } else {
        const recordsCount =
          response?.data?.totalRecords ||
          response?.data?.stats?.totalBoards ||
          0
        setTotalRecords(recordsCount)
        setTotalPages(Math.ceil(recordsCount / pageSize) || 1)
      }
    } catch (err: any) {
      console.error(err)
      setError('Failed to fetch patients')
    } finally {
      setLoadingForShimmer(false)
      setLoading(false)
    }
  }

  const handleOpenDialog = () => {
    setEditData(null)
    setFormData({ name: '', description: '' })
    setOpenDialog(true)
  }

  const handleEditBoard = (board: any) => {
    setEditData(board)
    setFormData({
      name: board.name || '',
      description: board.description || '',
    })
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditData(null)
    setFormData({ name: '', description: '' })
  }

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter name')
      return
    }
    if (!formData.description) {
      toast.error('Please enter description')
      return
    }

    const payload = { ...formData }

    try {
      if (editData) {
        setAddDialogLoading(true)
        const response = await api.patch(
          `boards/licensetype/${editData._id}`,
          payload
        )
        if (response.status === 200) {
          toast.success('Board Name updated successfully')
          handleCloseDialog()
          fetchBoardName()
        }
      } else {
        setAddDialogLoading(true)
        const response = await api.post('boards/licensetype', payload)
        if (response.status === 201) {
          toast.success('Board Name added successfully')
          handleCloseDialog()
          fetchBoardName()
        }
      }
    } catch (error: any) {
      console.error('Error adding board:', error)
      toast.error(error?.response?.data?.message || 'Failed to save board')
    } finally {
      setAddDialogLoading(false)
    }
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialog(false)
  }

  const handleConfirmDelete = async () => {
    if (!selectedBoardForDelete) {
      toast.error('No board selected for deletion')
      return
    }

    try {
      setDeleteLoading(true)
      const response = await api.delete(
        `boards/licensetype/${selectedBoardForDelete._id}`
      )
      if (response.status === 200) {
        toast.success('Board deleted successfully')
        handleCloseDeleteDialog()
        fetchBoardName()
      }
    } catch (error: any) {
      console.error('Error deleting board:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete board')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleCloseToggle = () => {
    setOpenToggle(false)
  }

  const handleConfirmToggle = async () => {
    if (!toggleData) {
      toast.error('No board selected for toggle')
      return
    }

    try {
      setLoading(true)

      const response = await api.patch(`boards/licensetype/${toggleData._id}`, {
        isActive: !toggleData.isActive,
      })
      if (response.status === 200) {
        toast.success('Board status updated successfully')
        handleCloseToggle()
        fetchBoardName()
      }
    } catch (error: any) {
      console.error('Error toggling board:', error)
      toast.error(error?.response?.data?.message || 'Failed to toggle board')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setSearchKeyword('')
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }

  const columns = [
    {
      header: 'NAME',
      accessor: 'name',
      sortable: true,
    },

    {
      header: 'DESCRIPTION',
      accessor: 'description',
      sortable: true,
    },

    {
      header: 'ACTIONS',
      render: board => (
        <div className="flex items-center justify-center gap-1">
          <Tooltip title={board?.isActive ? 'Deactivate' : 'Activate'} arrow>
            <IconButton
              onClick={() => {
                setToggleData(board)
                setOpenToggle(true)
              }}
            >
              {board?.isActive ? (
                <ToggleOnIcon sx={{ fontSize: 32, color: '#10b981' }} />
              ) : (
                <ToggleOffIcon sx={{ fontSize: 32, color: '#ef4444' }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit" arrow>
            <IconButton
            onClick={() => handleEditBoard?.(board)}
             sx={{ color: '#10b981' }}>
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete" arrow>
            <IconButton
            onClick={() => { setSelectedBoardForDelete(board); setDeleteDialog(true) }}
             sx={{ color: '#dc2626' }}>
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const handleSort = (field: string) => {
  let order = "asc"

  if (sortField === field) {
    order = sortOrder === "asc" ? "desc" : "asc"
  }

  setSortField(field)
  setSortOrder(order)
}

  return (
    <div>
      <BordMegementHeader
        handleOpenDialog={handleOpenDialog}
        title="License Type"
        buttonText="Add License Type"
      >
        <UploadCsv
          apiUrl="boards/upload-licensetype-csv"
          title="Import License Type"
        />

        <ExportCsv
          apiUrl="boards/export-licensetype"
          fileName="license_type.csv"
        />
      </BordMegementHeader>
      ;
      <BordFilterCard
        searchLabel="Search Name"
        search={searchKeyword}
        setSearchKeyword={val => {
          setSearchKeyword(val)
          setCurrentPage(1)
        }}
        handleReset={() => {
          handleReset()
          setCurrentPage(1)
        }}
      />
      <ReusableTable columns={columns} data={boardData} loading={loading} onSort={handleSort}
sortField={sortField}
sortOrder={sortOrder}/>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
                    {/* <RowsPerPageDropdown
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                    /> */}
                </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>
      <BordAddandEditDialogComponent
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        addDialogLoading={addDialogLoading}
        handleSubmit={handleSubmit}
        editData={editData}
        formData={formData}
        setFormData={setFormData}
      />
      <BordDeleteDialogComponent
        deleteDialog={deleteDialog}
        handleCloseDeleteDialog={handleCloseDeleteDialog}
        deleteLoading={deleteLoading}
        handleConfirmDelete={handleConfirmDelete}
        loading={deleteLoading}
      />
      <ActiveInactiveComponent
        isActive={toggleData?.isActive}
        setOpenToggle={setOpenToggle}
        handleConfirmToggle={handleConfirmToggle}
        loading={loading}
        openToggle={openToggle}
      />
    </div>
  )
}

export default LicenseType
